import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PushToggle from "../components/PushToggle";
import { CalendarDays, Car, MapPin, ShieldCheck, BadgeCheck, ChevronRight, Bell } from "lucide-react";
import { format } from "date-fns";
import api, { formatZAR } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data || [])).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data || [])).catch(() => {});
  }, []);

  const now = new Date();
  const upcoming = bookings.filter((b) => new Date(b.end_date) >= now && b.status !== "cancelled");
  const past = bookings.filter((b) => new Date(b.end_date) < now || b.status === "cancelled");
  const list = tab === "upcoming" ? upcoming : past;

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Your dashboard</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#08153a] mt-2">
              Welcome back, {(user?.name || "driver").split(" ")[0]}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <PushToggle compact />
            <Link to="/vehicles" className="btn-primary text-sm" data-testid="dashboard-book-cta">Find your next ride</Link>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Upcoming trips" value={upcoming.length} icon={<CalendarDays size={18} />} />
          <Stat label="Past trips" value={past.length} icon={<Car size={18} />} />
          <Stat label="Verifications" value={user?.id_verified ? "Verified" : "Pending"} icon={<ShieldCheck size={18} />} />
          <Stat label="Notifications" value={notifs.filter((n) => !n.read).length} icon={<Bell size={18} />} />
        </div>

        {/* Tabs */}
        <div className="mt-10 border-b border-[#E2E8F0] flex gap-6">
          {[
            { key: "upcoming", label: `Upcoming (${upcoming.length})` },
            { key: "past", label: `Past (${past.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#1d7ffd] text-[#08153a]" : "border-transparent text-[#64748B] hover:text-[#08153a]"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="mt-10 border border-[#E2E8F0] rounded-2xl p-10 text-center bg-[#F7F8FA]">
            <p className="font-display font-bold text-[#08153a] text-lg">No {tab} trips yet</p>
            <p className="text-sm text-[#64748B] mt-1">Find a car you love and book in minutes.</p>
            <Link to="/vehicles" className="btn-primary text-sm mt-5 inline-flex">Browse cars</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {list.map((b) => (
              <Link
                key={b.id}
                to={`/bookings/${b.id}`}
                className="group flex items-center gap-5 p-4 rounded-2xl border border-[#E2E8F0] hover:shadow-md transition-shadow"
                data-testid={`booking-row-${b.id}`}
              >
                <div className="w-28 sm:w-40 aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F2F5] flex-shrink-0">
                  {b.vehicle_photo ? (
                    <img src={b.vehicle_photo} alt={b.vehicle_title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#64748B]"><Car size={22} /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-[#08153a] truncate">{b.vehicle_title}</h3>
                    <StatusPill status={b.status} />
                  </div>
                  <p className="text-xs text-[#64748B] mt-1 inline-flex items-center gap-1">
                    <CalendarDays size={12} /> {format(new Date(b.start_date), "d MMM")} – {format(new Date(b.end_date), "d MMM yyyy")}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#64748B]">
                    {b.with_driver && <span className="inline-flex items-center gap-1"><BadgeCheck size={12} className="text-[#1d7ffd]" /> Chauffeur</span>}
                    {b.with_delivery && <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-[#1d7ffd]" /> Delivery</span>}
                  </div>
                </div>
                <div className="hidden sm:block text-right">
                  <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Total</p>
                  <p className="font-display font-black text-[#08153a]">{formatZAR(b.pricing?.total_due)}</p>
                </div>
                <ChevronRight size={18} className="text-[#64748B] group-hover:text-[#08153a]" />
              </Link>
            ))}
          </div>
        )}

        {/* Notifications */}
        {notifs.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display text-2xl font-bold text-[#08153a]">Notifications</h2>
            <div className="mt-4 space-y-2">
              {notifs.slice(0, 6).map((n) => (
                <div key={n.id} className={`p-4 rounded-xl border ${n.read ? "border-[#E2E8F0] bg-white" : "border-[#1d7ffd]/30 bg-[#1d7ffd]/5"}`}>
                  <p className="font-semibold text-[#08153a] text-sm">{n.title}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{n.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-[#E2E8F0] p-5">
    <div className="text-[#1d7ffd]">{icon}</div>
    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-3">{label}</p>
    <p className="font-display text-2xl font-black text-[#08153a] mt-1">{value}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    confirmed: { bg: "bg-[#10B981]/15", text: "text-[#10B981]", label: "Confirmed" },
    pending_payment: { bg: "bg-[#F59E0B]/15", text: "text-[#B45309]", label: "Pending payment" },
    cancelled: { bg: "bg-[#EF4444]/15", text: "text-[#B91C1C]", label: "Cancelled" },
    completed: { bg: "bg-[#08153a]/10", text: "text-[#08153a]", label: "Completed" },
  };
  const s = map[status] || { bg: "bg-[#F0F2F5]", text: "text-[#08153a]", label: status };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${s.bg} ${s.text}`}>{s.label}</span>;
};
